import type { Maybe } from '@adornis/base/utilTypes.js';
import { EmptyState } from '@adornis/components/client/EmptyState.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { EntityVersions } from '@adornis/versioned-entity/client/EntityVersions.js';
import type { VersionEvent } from '@adornis/versioned-entity/client/model/versionEvents.js';
import { VersionQuickFilters } from '@adornis/versioned-entity/client/VersionQuickFilters.js';
import { diffView } from '@adornis/versioned-entity/client/views/diffView.js';
import { addPageHeaderAdditional, removePageHeaderAdditional } from '@martinus/styles/client/PageHeader.js';
import { html } from 'lit';
import { keyed } from 'lit/directives/keyed.js';

/** History rows still carry the old company name — show it as Martinus, keeping the original casing. */
const martinize = (text: string) =>
  text.replace(/adornis/gi, match => {
    if (match === match.toUpperCase()) return 'MARTINUS';
    if (match[0]?.toUpperCase() === match[0]) return 'Martinus';
    return 'martinus';
  });

/**
 * The Verlauf of several documents as one list, with its search and quick filters in the page header. Shared by the
 * person and the group page, which only differ in which documents belong to them and how a version is labelled.
 */
export const HistoryTimeline = stateful(
  (props: {
    documentIDs: Maybe<string[]>;
    loading: boolean;
    /** Which mandate, contract or booking a version belongs to — the history only knows document ids. */
    resolveReference: (event: VersionEvent) => Maybe<string>;
  }) => {
    // The versions component filters but renders no field of its own, so the search lives in the
    // page header — same as the Rechteverwaltung matrix.
    const [search, setSearch] = useState('');

    // Bumped whenever a quick filter rewrites the search, so the field shows the rewritten text.
    const [inputGeneration, setInputGeneration] = useState(0);
    const prefillSearch = (next: string) => {
      setSearch(next);
      setInputGeneration(inputGeneration + 1);
    };

    useEffect(() => {
      addPageHeaderAdditional(
        1,
        html`
          <div class="px-4 pt-4 pb-2 flex flex-row items-center gap-4">
            ${keyed(
              inputGeneration,
              TextInput({
                placeholder: 'Feld, Wert, Person oder Datum suchen',
                placeholderMode: 'prefix',
                value: search,
                // Filtering is client-side, but a hit auto-opens its version — so let typing settle first.
                debounceMs: 200,
                onChanged: value => setSearch(value ?? ''),
              }),
            )}
          </div>
        `,
      );
      return () => {
        removePageHeaderAdditional(1);
      };
      // Not keyed on `search`: the input owns its value while typing and its debounced emissions lag
      // behind, so re-registering per keystroke would reset it to stale text. A quick filter instead
      // remounts it (`keyed`) with the search it rewrote.
    }, [inputGeneration]);

    // The chips only write into the search (`typ:SepaMandat`, `aktion:angelegt`); they follow every change of it.
    useEffect(() => {
      addPageHeaderAdditional(
        2,
        html`
          <div class="px-4 pb-4">
            ${VersionQuickFilters({
              documentID: props.documentIDs ?? [],
              searchTerm: search,
              onSearchTermChange: prefillSearch,
              transformText: martinize,
            })}
          </div>
        `,
      );
      return () => {
        removePageHeaderAdditional(2);
      };
      // by the joined ids, as a page may derive a fresh array per render
    }, [search, props.documentIDs?.join(';'), inputGeneration]);

    if (props.loading)
      return html`
        <div class="w-full h-full flex items-center justify-center">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      `;

    if (!props.documentIDs?.length) return EmptyState({ title: 'Keine Verlaufsinformationen verfügbar' });

    return html`
      <div class="card-container">
        ${EntityVersions({
          documentID: props.documentIDs,
          view: diffView(),
          searchTerm: search,
          transformText: martinize,
          resolveReference: props.resolveReference,
        })}
      </div>
    `;
  },
);
